import type { DocCollection, DocMeta } from '@blocksuite/store';

import { useBlockSuiteDocMeta } from './use-block-suite-page-meta';

/**
 * Hook for getting active (non-trashed, non-temp) pages
 * Used by page lists, services, and most components
 */
export function useActivePages(docCollection: DocCollection): DocMeta[] {
  return useBlockSuiteDocMeta(docCollection);
}

/**
 * Hook for getting ALL pages including temp docs
 * Used by components that need access to temp:peek docs (like TodoItemComponent)
 */
export function useAllPages(docCollection: DocCollection): DocMeta[] {
  // Get all pages without filtering temp:peek, only exclude trash
  return Array.from(docCollection.meta.docMetas).filter(docMeta => !docMeta.trash);
}

/**
 * Get active page IDs from DocCollection directly (for services that can't use hooks)
 * Excludes trash and temp:peek pages
 */
export function getActivePageIds(docCollection: DocCollection): string[] {
  return Array.from(docCollection.meta.docMetas)
    .filter((docMeta) => {
      if (docMeta.trash) return false;

      const tags = docMeta.tags || [];
      if (tags.includes('temp:peek')) return false;

      return true;
    })
    .map((docMeta) => docMeta.id);
}

/**
 * Get ALL page IDs including temp docs (for components that need temp:peek access)
 * Only excludes trash pages
 */
export function getAllPageIds(docCollection: DocCollection): string[] {
  return Array.from(docCollection.meta.docMetas)
    .filter((docMeta) => !docMeta.trash)
    .map((docMeta) => docMeta.id);
}

/**
 * Check if a specific page is active (for services)
 */
export function isPageActive(docMeta: DocMeta): boolean {
  if (docMeta.trash) return false;

  const tags = docMeta.tags || [];
  if (tags.includes('temp:peek')) return false;

  return true;
}
